import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import useGoBack from "@/hooks/useGoBack";
import { toast } from "@/lib/toast";
import { ArrowLeft, Trash2, Search, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import SearchClearButton from "@/components/ui/SearchClearButton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HeaderProfileGroup from "@/components/TopBar";
import StatusBadge from "@/components/tasks/StatusBadge";
import TrashPurgeBanner from "@/components/shared/TrashPurgeBanner";

/**
 * TasksTrash — Corbeille du Module Tâches (spec 2026-02-17).
 *
 * Read-only listing of soft-deleted tasks. Each row shows the deletion
 * timestamp, who deleted it, and the motif captured at delete-time.
 * Same "no permanent delete" policy as Documents: consultation only. A
 * future admin-only "Purger" action would go here, but restoration is
 * not exposed until we agree on a UX for it.
 */
export default function TasksTrash() {
  const goBack = useGoBack("/taches");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/tasks/trash/list");
        setItems(data || []);
      } catch (err) {
        toast.error("Impossible de charger la corbeille", { description: err.response?.data?.detail || "" });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((t) =>
      `${t.reference || ""} ${t.subject || ""} ${t.deleted_reason || ""} ${t.deleted_by_name || ""}`
        .toLowerCase().includes(term)
    );
  }, [items, q]);

  const isAdmin = user?.role === "admin";

  const restoreTask = async (taskId) => {
    if (!isAdmin) return;
    try {
      await api.post(`/tasks/${taskId}/restore`);
      setItems((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Tâche restaurée", { description: "Les rappels futurs sont réactivés automatiquement" });
    } catch (err) {
      toast.error("Impossible", { description: err.response?.data?.detail || "" });
    }
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="flex flex-col gap-4 min-h-0" style={{ height: "calc(100vh - 80px)" }} data-testid="tasks-trash-page">
      {/* Header */}
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack} className="gap-1" data-testid="trash-back">
            <ArrowLeft size={14} /> Tâches
          </Button>
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #EF4444 0%, #7F1D1D 100%)" }}
          >
            <Trash2 size={20} color="#fff" />
          </div>
          <div>
            <h1 className="text-3xl font-bold leading-none">Corbeille</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {items.length} tâche{items.length > 1 ? "s" : ""} supprimée{items.length > 1 ? "s" : ""} · purge automatique à 30 jours{isAdmin ? " · restauration possible" : ""}
            </p>
          </div>
        </div>
        <HeaderProfileGroup />
      </div>

      {/* Search bar */}
      <div className="relative w-full max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher réf, objet, motif ou auteur…"
          className="pl-9 pr-9"
          data-testid="trash-search"
        />
        <SearchClearButton value={q} onClear={() => setQ("")} testid="trash-search-clear" />
      </div>

      {/* Bannière de préavis purge (≤ 3 jours) */}
      <TrashPurgeBanner items={items} noun="tâche" nounFemale />


      {/* Table */}
      <Card className="bg-card border-vertec-borderDark flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="flex-1 min-h-0" style={{ overflowY: "auto", overflowX: "hidden" }}>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground italic">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground italic">
              {items.length === 0 ? "Aucune tâche dans la corbeille." : "Aucun résultat pour cette recherche."}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[110px]">Réf.</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead>Dernier statut</TableHead>
                  <TableHead>Supprimée le</TableHead>
                  <TableHead>Par</TableHead>
                  <TableHead>Motif</TableHead>
                  {isAdmin && <TableHead className="w-[110px] text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} className="hover:bg-white/[0.02]" data-testid={`trash-row-${t.id}`}>
                    <TableCell>
                      <span className="text-[11px] font-mono font-bold text-vertec-yellow tabular-nums">
                        {t.reference}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{t.subject}</TableCell>
                    <TableCell><StatusBadge status={t.effective_status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(t.deleted_at)}</TableCell>
                    <TableCell className="text-sm">{t.deleted_by_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate" title={t.deleted_reason || ""}>
                      {t.deleted_reason || <em>—</em>}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreTask(t.id)}
                          className="gap-1 border-status-success/40 text-status-success hover:bg-status-success/10 h-7 px-2 text-[11px]"
                          data-testid={`trash-restore-${t.id}`}
                          title="Restaurer et réactiver les rappels futurs"
                        >
                          <RotateCcw size={11} /> Restaurer
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
